import { useCallback, useEffect, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { cn } from '@/lib/utils'
import { useStewardStore, type Message } from '@/stores/useStewardStore'
import { Scissors, X } from 'lucide-react'
import {
  streamSteward,
  listStewardSessions,
  getStewardSession,
  patchStewardSession,
  deleteStewardSession,
  getStewardTriggers,
  type StreamStewardParams,
  type StewardTriggerRegistry,
} from '@/lib/api'
import { StewardMessage } from './components/StewardMessage'
import { StewardInput } from './components/StewardInput'
import { StewardTimeline } from './components/StewardTimeline'
import { StewardSessionPicker } from './components/StewardSessionPicker'
import { InboxButton } from './components/InboxButton'
import {
  fileToAttachment,
  MAX_ATTACHMENTS,
  MAX_ATTACHMENT_BYTES,
  type PendingAttachment,
} from './attachments'

/**
 * steward — chat-only surface for the self-governing Johnny.Decimal filing
 * assistant. Drop a thought → it files a Job and tells you what + why; or ask →
 * it surfaces the one right Job. No filing-tree browser, no forms.
 *
 * Streaming orchestration lives here (like the concierge's ConciergeChat); the
 * store exposes pure state actions. There's no incremental assistant text in
 * v1 — while a turn runs we show a live folding timeline (one row per node:
 * running → thinking → tool → done), then render session_complete.response with
 * the now-folded timeline pinned above it.
 *
 * Persisted messages rehydrate automatically via the Zustand persist
 * middleware — transient streaming state is never persisted, so a reload comes
 * back idle with the conversation intact (no "auto-load" fetch needed).
 */
/**
 * The Job the consumer is looking at RIGHT NOW — the most-recently-rendered Job card,
 * scanning newest→oldest. Sent with each turn so a bare "delete this" / "open it" resolves
 * server-side to the on-screen Job instead of a whole-map query + "which one?".
 *
 * Covers all three ways a Job card reaches the screen:
 *   • a deep-link / opened Job card   → `card.kind === 'job'`            (path = card.path)
 *   • a clicked job reference link    → `card.kind === 'note'`, job kind (path = card.id)
 *   • a ```steward-card``` fence in an assistant reply                   (path = fence body)
 * Returns null when no Job is on screen (a project/area/category card is NOT a job).
 */
function activeJobPath(messages: Message[]): string | null {
  for (let i = messages.length - 1; i >= 0; i--) {
    const m = messages[i]
    if (m.card?.kind === 'job') return m.card.path
    if (m.card?.kind === 'note' && m.card.noteKind === 'job') return m.card.id
    if (m.role === 'assistant' && m.content) {
      const fences = [...m.content.matchAll(/```steward-card\s*\n([\s\S]*?)```/g)]
      const last = fences[fences.length - 1]
      const path = last?.[1].trim()
      if (path) return path
    }
  }
  return null
}

export function StewardPage() {
  const messages = useStewardStore((s) => s.messages)
  const status = useStewardStore((s) => s.status)
  const streamingSteps = useStewardStore((s) => s.streamingSteps)
  const error = useStewardStore((s) => s.error)
  const sessions = useStewardStore((s) => s.sessions)
  const sessionName = useStewardStore((s) => s.sessionName)
  const sessionId = useStewardStore((s) => s.sessionId)

  const appendUserTurn = useStewardStore((s) => s.appendUserTurn)
  const startStreaming = useStewardStore((s) => s.startStreaming)
  const startStep = useStewardStore((s) => s.startStep)
  const appendThinking = useStewardStore((s) => s.appendThinking)
  const addTool = useStewardStore((s) => s.addTool)
  const completeStep = useStewardStore((s) => s.completeStep)
  const finishStreaming = useStewardStore((s) => s.finishStreaming)
  const setError = useStewardStore((s) => s.setError)
  const setSessions = useStewardStore((s) => s.setSessions)
  const setSessionName = useStewardStore((s) => s.setSessionName)
  const loadSession = useStewardStore((s) => s.loadSession)
  const newSession = useStewardStore((s) => s.newSession)
  const openJobCard = useStewardStore((s) => s.openJobCard)
  const specialistActive = useStewardStore((s) => s.specialistActive)
  const specialistHarness = useStewardStore((s) => s.specialistHarness)
  const setSpecialist = useStewardStore((s) => s.setSpecialist)

  // The `/` trigger registry (specialist harnesses + their tasks) — powers the suggestion tree.
  const [triggers, setTriggers] = useState<StewardTriggerRegistry | null>(null)
  useEffect(() => {
    getStewardTriggers().then(setTriggers).catch(() => { /* tree is optional */ })
  }, [])
  const [searchParams, setSearchParams] = useSearchParams()

  const [draft, setDraft] = useState('')
  const [runMode, setRunMode] = useState<'fast' | 'deep'>('fast')
  const [decisionOptions, setDecisionOptions] = useState<string[]>([])  // clickable chips for an open inbox DECISION
  const [pending, setPending] = useState<PendingAttachment[]>([])

  // Stage dropped/pasted/picked files (skip oversize, cap the count). Best-effort per file.
  const addFiles = useCallback(async (files: File[]) => {
    const room = MAX_ATTACHMENTS - pending.length
    if (room <= 0) return
    const accepted: PendingAttachment[] = []
    for (const f of files.slice(0, room)) {
      if (f.size > MAX_ATTACHMENT_BYTES) {
        setError(`${f.name} is too large (max 15 MB)`)
        continue
      }
      try {
        accepted.push(await fileToAttachment(f))
      } catch {
        setError(`Couldn't read ${f.name}`)
      }
    }
    if (accepted.length) setPending((p) => [...p, ...accepted])
  }, [pending.length, setError])
  const scrollRef = useRef<HTMLDivElement>(null)
  // Stick-to-bottom: starts true so a fresh load / session-switch lands at the bottom
  // and STAYS there as async job cards finish loading and grow the content. Flips false
  // only when the user scrolls up; flips back true when they return to the bottom.
  const stickRef = useRef(true)

  const isStreaming = status === 'streaming'
  // Latest isStreaming for the ResizeObserver callback (which isn't re-subscribed per render).
  const isStreamingRef = useRef(isStreaming)
  useEffect(() => {
    isStreamingRef.current = isStreaming
  }, [isStreaming])

  // Refresh the picker list + the active session's (possibly auto-assigned) name.
  // Best-effort — the chat works even if this fails.
  const refreshSessions = useCallback(async () => {
    try {
      const list = await listStewardSessions()
      setSessions(list)
      const sid = useStewardStore.getState().sessionId
      const cur = list.find((s) => s.session_id === sid)
      if (cur) setSessionName(cur.name)
    } catch (e) {
      // Best-effort, but don't fly blind — surface it to the console.
      console.warn('[steward] session list refresh failed:', e)
    }
  }, [setSessions, setSessionName])

  // Load the session list on mount. The active conversation rehydrates from the
  // persisted store, so we don't auto-switch here — we just populate the picker.
  useEffect(() => {
    void refreshSessions()
  }, [refreshSessions])

  // Calendar deep link: /steward?job=<rel> opens that Job as a card in a fresh
  // session, then clears the param so a reload doesn't re-open it. Runs once.
  useEffect(() => {
    const jobPath = searchParams.get('job')
    if (jobPath) {
      openJobCard(jobPath)
      const next = new URLSearchParams(searchParams)
      next.delete('job')
      setSearchParams(next, { replace: true })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  async function handleSelectSession(id: string) {
    setDecisionOptions([])  // switching sessions clears any decision chips
    if (id === sessionId) return
    try {
      const detail = await getStewardSession(id)
      loadSession(detail)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load session')
    }
  }

  // Open an inbox item; for a DECISION, surface its options as clickable chips above the input.
  async function handleOpenInboxItem(id: string, options?: string[]) {
    await handleSelectSession(id)
    if (options && options.length) setDecisionOptions(options)
  }

  async function handlePin(id: string, pinned: boolean) {
    try {
      await patchStewardSession(id, { pinned })
      await refreshSessions()
    } catch {
      /* ignore */
    }
  }

  async function handleDelete(id: string) {
    try {
      await deleteStewardSession(id)
      if (id === sessionId) newSession()
      await refreshSessions()
    } catch {
      /* ignore */
    }
  }

  // A new turn (or a freshly loaded / switched session) re-anchors to the bottom — and
  // KEEPS pinning for ~1.2s as the async job cards trickle in and grow the content (the
  // refresh bug: cards loaded after the one-shot scroll and stranded the view mid-chat).
  // The loop pins every frame while stuck; a real upward gesture (below) ends it early.
  useEffect(() => {
    stickRef.current = true
    const el = scrollRef.current
    if (!el) return
    let raf = 0
    const start = performance.now()
    const step = () => {
      if (!stickRef.current) return
      el.scrollTop = el.scrollHeight
      if (performance.now() - start < 1200) raf = requestAnimationFrame(step)
    }
    raf = requestAnimationFrame(step)
    return () => cancelAnimationFrame(raf)
  }, [messages])

  // Anchor downward as content GROWS — live timeline rows + search notifications, and the
  // async job cards. A ResizeObserver fires AFTER each height change, so we re-pin reliably
  // while stuck or streaming. (overflow-anchor:none on the container stops the browser's
  // scroll-anchoring from shifting us off the bottom as content above grows.)
  useEffect(() => {
    const el = scrollRef.current
    const content = el?.firstElementChild
    if (!el || !content) return
    const pin = () => {
      if (isStreamingRef.current || stickRef.current) el.scrollTop = el.scrollHeight
    }
    const ro = new ResizeObserver(pin)
    ro.observe(content)
    return () => ro.disconnect()
  }, [])

  // Stick state changes ONLY on a real user gesture, never on programmatic/anchoring
  // scrolls (those were misread as "scrolled up" and stranded the refresh).
  //   • wheel-up / touch-drag → unstick (let me read)
  //   • scroll that lands back near the bottom → re-stick
  const onScroll = useCallback(() => {
    const el = scrollRef.current
    if (el && el.scrollHeight - el.scrollTop - el.clientHeight < 80) stickRef.current = true
  }, [])
  const onWheel = useCallback((e: React.WheelEvent) => {
    if (e.deltaY < 0) stickRef.current = false
  }, [])
  const onTouchMove = useCallback(() => {
    stickRef.current = false
  }, [])

  async function handleSend(explicit?: string) {
    const content = (explicit ?? draft).trim()
    const files = pending
    if ((!content && files.length === 0) || isStreaming) return

    setDecisionOptions([])  // answering clears the decision chips
    setDraft('')
    setPending([])
    // Show the user turn — text, or a paperclip line when it's files-only.
    appendUserTurn(content || `📎 ${files.map((f) => f.filename).join(', ')}`)
    startStreaming()

    const params: StreamStewardParams = {
      content,
      session_id: useStewardStore.getState().sessionId,
      run_mode: runMode,
      // The Job card on screen (the just-appended user turn carries no card, so the scan
      // lands on the prior assistant/deep-link card) → "delete this" resolves to it.
      active_job_path: activeJobPath(useStewardStore.getState().messages),
      attachments: files.map((f) => ({ filename: f.filename, mime: f.mime, data_base64: f.data_base64 })),
    }

    await streamSteward(params, {
      onStageStart: (stage) => startStep(stage),
      onThinkingDelta: (delta) => appendThinking(delta),
      onTool: (tool) => addTool(tool),
      onStage: (stage, output) => completeStep(stage, output),
      onSpecialist: (ev) => setSpecialist(ev),
      onComplete: ({ session_id, response }) =>
        finishStreaming({ response, sessionId: session_id }),
      onError: (msg) => setError(msg),
    })

    // The stream has fully closed — including the server-side auto-name that runs
    // in its tail — so refresh the picker + the active session's name now.
    await refreshSessions()
  }

  // The live in-flight block has a signal once the first node step arrives.
  const hasLiveSignal = streamingSteps.length > 0

  return (
    <div className="flex h-full flex-col">
      {/* Session header — picker + new conversation */}
      <div className="flex-shrink-0 border-b border-border">
        <div className="mx-auto flex w-full max-w-[680px] items-center justify-between px-3 py-1.5">
          <StewardSessionPicker
            name={sessionName}
            sessions={sessions}
            activeId={sessionId}
            onOpen={refreshSessions}
            onNew={newSession}
            onSelect={handleSelectSession}
            onPin={handlePin}
            onDelete={handleDelete}
          />
          <InboxButton onOpenItem={handleOpenInboxItem} />
        </div>
      </div>

      {/* Messages */}
      <div
        ref={scrollRef}
        onScroll={onScroll}
        onWheel={onWheel}
        onTouchMove={onTouchMove}
        className="flex-1 overflow-y-auto overscroll-contain [overflow-anchor:none]"
      >
        <div className="mx-auto w-full max-w-[680px] px-5 py-8">
          {messages.length === 0 && !isStreaming ? (
            <div className="py-20 text-center">
              <h2 className="mb-2 text-base font-light text-foreground">
                Drop a thought — I'll file it.
              </h2>
              <p className="mx-auto max-w-md text-[13px] font-light leading-relaxed text-muted-foreground">
                A thought, a commitment, a half-formed task. I file it as a Job
                and tell you what and why. Or ask, and I surface the one right
                Job.
              </p>
            </div>
          ) : (
            <div className="space-y-5">
              {messages.map((m) => (
                <StewardMessage key={m.id} message={m} />
              ))}

              {/* Live in-flight turn: the folding per-node timeline. */}
              {isStreaming && hasLiveSignal && (
                <div className="flex justify-start">
                  <div className="max-w-[85%] py-1">
                    <StewardTimeline steps={streamingSteps} live />
                  </div>
                </div>
              )}

              {/* Pulse dots before any signal arrives. */}
              {isStreaming && !hasLiveSignal && (
                <div className="flex justify-start">
                  <div className="flex items-center gap-1 py-2">
                    <span className="h-1 w-1 animate-pulse rounded-full bg-muted-foreground/50" />
                    <span
                      className="h-1 w-1 animate-pulse rounded-full bg-muted-foreground/50"
                      style={{ animationDelay: '150ms' }}
                    />
                    <span
                      className="h-1 w-1 animate-pulse rounded-full bg-muted-foreground/50"
                      style={{ animationDelay: '300ms' }}
                    />
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="mx-auto w-full max-w-[680px] flex-shrink-0 px-5">
          <div className="py-2 text-[12px] text-red-500/90">{error}</div>
        </div>
      )}

      {/* Input — hairline top divider, no full input border. */}
      <div className="flex-shrink-0 border-t border-border">
        <div className="mx-auto w-full max-w-[680px] px-5 py-3">
          {decisionOptions.length > 0 && (
            <div className="mb-2 flex flex-wrap gap-1.5">
              {decisionOptions.map((opt) => (
                <button
                  key={opt}
                  type="button"
                  disabled={isStreaming}
                  onClick={() => handleSend(opt)}
                  className="rounded-full border border-border bg-background px-3 py-1 text-xs text-foreground transition-colors hover:bg-muted disabled:opacity-50"
                >
                  {opt}
                </button>
              ))}
            </div>
          )}
          {/* Specialist (surgeon) mode banner — you're talking to the commonplace keeper, not the GP. */}
          {specialistActive && (
            <div className="mb-2 flex items-center justify-between rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-1.5 text-[12px]">
              <span className="inline-flex items-center gap-1.5 font-medium text-amber-700 dark:text-amber-400">
                <Scissors className="h-3.5 w-3.5" />
                {harnessLabel(specialistHarness, triggers)} · active
              </span>
              <button
                type="button"
                onClick={() => handleSend('/exit')}
                disabled={isStreaming}
                className="inline-flex items-center gap-1 rounded text-muted-foreground transition-colors hover:text-foreground disabled:opacity-50"
                aria-label="Exit the commonplace keeper, back to the generalist"
              >
                exit <X className="h-3.5 w-3.5" />
              </button>
            </div>
          )}

          {/* `/` suggestion tree — type `/` for harnesses; pick one, `/` again for its tasks. */}
          {(() => {
            if (!triggers || !draft.startsWith('/') || draft.includes(' ')) return null
            const [hPart, tPart] = draft.slice(1).split('/')
            const pick = (next: string) => setDraft(next)
            // Second segment present → suggest the chosen harness's tasks.
            if (tPart !== undefined) {
              const h = triggers.harnesses.find((x) => x.id === hPart.toLowerCase())
              if (!h) return null
              const tasks = h.tasks.filter((t) => t.id.startsWith(tPart.toLowerCase()))
              if (tasks.length === 0) return null
              return (
                <div className="mb-2 flex flex-wrap gap-1.5">
                  {tasks.map((t) => (
                    <button key={t.id} type="button" title={t.description}
                      onClick={() => pick(`/${h.id}/${t.id}${t.requires_text ? ' ' : ''}`)}
                      className="rounded-full border border-border bg-background px-3 py-1 text-xs text-foreground transition-colors hover:bg-muted">
                      /{h.id}/<span className="font-medium">{t.id}</span> — {t.label}
                    </button>
                  ))}
                </div>
              )
            }
            // First segment → suggest harnesses by prefix.
            const harnesses = triggers.harnesses.filter((x) => x.id.startsWith(hPart.toLowerCase()))
            if (harnesses.length === 0) return null
            return (
              <div className="mb-2 flex flex-wrap gap-1.5">
                {harnesses.map((h) => (
                  <button key={h.id} type="button" title={h.description}
                    onClick={() => pick(`/${h.id}/`)}
                    className="rounded-full border border-border bg-background px-3 py-1 text-xs text-foreground transition-colors hover:bg-muted">
                    /<span className="font-medium">{h.id}</span> — {h.label}
                  </button>
                ))}
              </div>
            )
          })()}

          <StewardInput
            value={draft}
            onChange={setDraft}
            onSubmit={handleSend}
            disabled={isStreaming}
            placeholder={
              specialistActive
                ? 'Talk to the commonplace keeper…'
                : messages.length === 0 ? "Drop a thought — I'll file it." : 'Talk to steward…'
            }
            attachments={pending}
            onAddFiles={addFiles}
            onRemoveAttachment={(i) => setPending((p) => p.filter((_, idx) => idx !== i))}
          />
          <div className="mt-2 flex items-center justify-end gap-1">
            <ModeToggle mode={runMode} onChange={setRunMode} disabled={isStreaming} />
          </div>
        </div>
      </div>
    </div>
  )
}

/** Friendly label for a specialist harness id (from the registry, else Title-Cased). */
function harnessLabel(id: string, registry: StewardTriggerRegistry | null): string {
  const h = registry?.harnesses.find((x) => x.id === id)
  if (h) return h.label
  return id
    ? id.split('_').map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')
    : 'Specialist'
}

/** Compact fast / deep run-mode toggle. Deep enables extended thinking. */
function ModeToggle({
  mode,
  onChange,
  disabled,
}: {
  mode: 'fast' | 'deep'
  onChange: (m: 'fast' | 'deep') => void
  disabled?: boolean
}) {
  return (
    <div className="inline-flex overflow-hidden rounded-full border border-border text-[11px]">
      {(['fast', 'deep'] as const).map((m) => (
        <button
          key={m}
          type="button"
          disabled={disabled}
          onClick={() => onChange(m)}
          className={cn(
            'px-2.5 py-0.5 font-light transition-colors disabled:opacity-50',
            mode === m
              ? 'bg-foreground text-background'
              : 'text-muted-foreground hover:text-foreground'
          )}
        >
          {m}
        </button>
      ))}
    </div>
  )
}
